// CRM-Leiste: eigener Look (salbeigrün), Daten aus admin.minimusiker.de, weitere Gespräche mit der Einrichtung.
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { TEAM, isUserKey } from '@mail/shared';
import { toast } from './Toast.tsx';
import { buchungslink as buchungslinkAus } from '@mail/shared';
import { api, type CrmInfo } from '../lib/api.ts';
import { fmtWhen } from '../lib/format.ts';
import { TerminAkteBlock } from './TerminAkte.tsx';
import { onThreadsChanged, toggleCrm, useStore } from '../lib/store.ts';

const eur = (n: number) => n.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' });
const datum = (iso: string | null | undefined) =>
  iso
    ? new Date(iso + (iso.length === 10 ? 'T00:00:00' : '')).toLocaleDateString('de-DE', {
        weekday: 'short',
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      })
    : '';
const inTagen = (iso: string) => {
  const d = Math.round((new Date(iso + 'T00:00:00').getTime() - Date.now()) / 864e5);
  return d < 0 ? `vor ${-d} Tagen` : d === 0 ? 'heute' : `in ${d} Tagen`;
};

export function CrmPanel({ threadId }: { threadId: number | null }) {
  const nav = useNavigate();
  const mailboxes = useStore((s) => s.mailboxes);
  const [info, setInfo] = useState<CrmInfo | null>(null);
  const [laden, setLaden] = useState(false);
  const [sum, setSum] = useState<{ text: string } | 'laden' | null>(null);

  const load = (frisch = false) => {
    if (!threadId) return setInfo(null);
    setLaden(true);
    api
      .crm(threadId, frisch)
      .then(setInfo)
      .catch(() => setInfo({ available: true, error: 'CRM nicht erreichbar' }))
      .finally(() => setLaden(false));
  };
  useEffect(() => {
    setSum(null);
    load();
  }, [threadId]);
  useEffect(
    () => onThreadsChanged((ids) => threadId != null && ids.includes(threadId) && load()),
    [threadId],
  );

  const Head = (
    <div className="crm-head">
      CRM · admin.minimusiker.de
      <button onClick={toggleCrm} aria-label="CRM-Leiste schließen" title="Schließen">
        ×
      </button>
    </div>
  );
  if (!threadId)
    return (
      <aside className="crm" aria-label="CRM-Kontext">
        {Head}
        <div className="blk">
          <p className="empty">
            Hier steht, wer schreibt: Einrichtung, Termin, Modell, Deal, Erinnerungen und was
            automatisch rausging.
          </p>
        </div>
      </aside>
    );
  if (!info || laden)
    return (
      <aside className="crm" aria-label="CRM-Kontext">
        {Head}
        <p className="empty">{laden ? 'Lädt CRM-Kontext …' : ''}</p>
      </aside>
    );
  if (!info.available)
    return (
      <aside className="crm" aria-label="CRM-Kontext">
        {Head}
        <p className="empty">
          {info.reason === 'hoerthin'
            ? 'Dieses Postfach gehört zur Hört Hin! GmbH. Dafür gibt es keinen CRM-Kontext.'
            : info.reason === 'junk'
              ? 'Junk wird nicht mit dem CRM abgeglichen.'
              : info.reason === 'info'
                ? 'Info-Mail ohne Bezug zu einer Einrichtung.'
                : 'Für dieses Postfach ist Bob ausgeschaltet.'}
        </p>
      </aside>
    );

  const k = info.kontext;
  const buchungslink = buchungslinkAus(k);
  const e = k?.einrichtung;
  const kandidaten = k?.match.kandidaten ?? [];
  const unsicher = !info.link && kandidaten.length > 0;

  return (
    <aside className="crm" aria-label="CRM-Kontext">
      {Head}
      {info.error && <div className="callout warn">⚠ {info.error}</div>}
      {!e && !unsicher && (
        <div className="blk">
          <p className="empty">
            <b>{info.from}</b> ist keinem Kontakt im CRM zugeordnet.
          </p>
          <p className="empty">
            Bob kann daraus einen Lead machen (mit Bestätigungskarte) – über „✦ Bob fragen“ im
            Antwortfeld: „Leg einen Lead an.“
          </p>
        </div>
      )}
      {unsicher && (
        <div className="blk">
          <div className="callout info">
            <b>Gehört {info.from} zu …?</b> Zuordnung unsicher (
            {k?.match.art === 'domain' ? 'gleiche Domain' : 'mehrere Treffer'}). Bitte bestätigen:
          </div>
          {kandidaten.map((c) => (
            <button
              key={c.id}
              className="rel-it"
              onClick={() =>
                void api
                  .crmLink(threadId, { institutionId: c.id, name: c.name, scope: 'email' })
                  .then(() => load())
              }
            >
              <span className="rel-r">{c.name}</span>
              <span className="rel-w">{c.ort}</span>
            </button>
          ))}
        </div>
      )}
      {e && (
        <>
          <div className="blk">
            <h2>{e.name}</h2>
            <div className="sub">
              {[e.plz, e.ort].filter(Boolean).join(' ')}
              {e.art ? ` · ${e.art}` : ''}
              {e.kinder ? ` · ca. ${e.kinder} Kinder` : ''}
              {e.region ? ` · Region ${e.region}` : ''}
            </div>
            {info.link && !info.link.confirmed && (
              <div
                className="callout info"
                style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}
              >
                <span>Automatisch zugeordnet. Stimmt das?</span>
                <button
                  className="btn sm pri"
                  onClick={() =>
                    void api
                      .crmLink(threadId, {
                        institutionId: e.id,
                        name: e.name,
                        ...(k?.kontakt?.id ? { contactId: k.kontakt.id } : {}),
                        scope: 'email',
                      })
                      .then(() => load())
                  }
                >
                  Ja
                </button>
                <button
                  className="btn sm"
                  onClick={() => void api.crmUnlink(threadId).then(() => load())}
                >
                  Nein
                </button>
              </div>
            )}
          </div>
          {e.telefon && (
            <div className="callrow">
              <div>
                <div className="blk-h">Telefon</div>
                <a className="tel" href={`tel:${e.telefon.replace(/[^\d+]/g, '')}`}>
                  {e.telefon}
                </a>
              </div>
              <button
                className="btn sm"
                onClick={() => void navigator.clipboard.writeText(e.telefon)}
                title="Nummer kopieren"
              >
                Kopieren
              </button>
              <button
                className="btn sm"
                title="Gesprächsnotiz in den CRM-Verlauf (über Bob, ohne Rückfrage)"
                onClick={() => {
                  const text = prompt('Gesprächsnotiz (kommt in den CRM-Verlauf der Einrichtung):');
                  if (!text?.trim()) return;
                  const min = Number(prompt('Dauer in Minuten (optional):', '') ?? '') || undefined;
                  void api
                    .gespraechsnotiz(threadId, {
                      text: text.trim(),
                      ...(min ? { dauerMin: min } : {}),
                    })
                    .then(() => toast('Gesprächsnotiz im CRM-Verlauf'))
                    .catch((err: Error) => toast(err.message));
                }}
              >
                📞 Notiz
              </button>
            </div>
          )}
          <dl className="kv">
            <dt>Kontakt</dt>
            <dd>
              {k?.kontakt
                ? `${k.kontakt.name}${k.kontakt.rolle ? ` (${k.kontakt.rolle})` : ''}`
                : '—'}
              {k?.kostentraeger ? ` · Kostenträger ${k.kostentraeger.name}` : ''}
            </dd>
            <dt>Lead</dt>
            <dd>
              {k?.lead
                ? `${k.lead.status}${k.lead.stufe ? ` · ${k.lead.stufe}` : ''}${k.lead.naechsterSchritt ? ` · ${k.lead.naechsterSchritt}` : ''}${k.lead.ferien ? ` · ${k.lead.ferien}` : ''}`
                : 'kein Lead'}
            </dd>
            <dt>Deal</dt>
            <dd>
              {k?.deals?.length
                ? k.deals.map((d) => (
                    <div key={d.id}>
                      <a href={d.crmUrl} target="_blank" rel="noopener">
                        {d.name}
                      </a>
                      <br />
                      <span className="muted">
                        {d.status} · Soll {eur(d.soll)} · berechnet {eur(d.berechnet)} · offen{' '}
                        {eur(d.offen)}
                        {d.unbezahlt ? ` · unbezahlt ${eur(d.unbezahlt)}` : ''}
                      </span>
                    </div>
                  ))
                : 'kein Vorgang'}
            </dd>
          </dl>
          {(k?.termine ?? []).slice(0, 3).map((t) => (
            <div className="blk" key={t.id}>
              <div className="blk-h">Termin</div>
              <div className="ev">
                <div className="d">
                  {datum(t.datum)} <span className="pill mut">{inTagen(t.datum)}</span>
                </div>
                <div>
                  {t.typ} · <b>{t.modell}</b>
                  {t.staff ? ` · ${t.staff}` : ''}
                  {t.status ? ` · ${t.status}` : ''}
                </div>
                {t.allinFensterBis && t.modell !== 'ALL-IN' && (
                  <div
                    className={`callout ${new Date(t.allinFensterBis) >= new Date(new Date().toDateString()) ? 'warn' : 'info'}`}
                  >
                    ALL-IN-Wechselfenster{' '}
                    {new Date(t.allinFensterBis) >= new Date(new Date().toDateString())
                      ? 'offen'
                      : 'war offen'}{' '}
                    bis {datum(t.allinFensterBis)}
                  </div>
                )}
                <a className="link" href={t.crmUrl} target="_blank" rel="noopener">
                  Termin im CRM ↗
                </a>
                <TerminAkteBlock eventId={t.id} />
              </div>
            </div>
          ))}
          {(k?.angeboteOffen ?? []).length > 0 && (
            <div className="callout info">
              Offenes Angebot:{' '}
              {k
                .angeboteOffen!.map((a) => `${a.nummer} (${eur(a.brutto)}, ${a.status})`)
                .join(', ')}
            </div>
          )}
          {info.related && info.related.length > 0 && (
            <div className="blk">
              <div className="blk-h">Weitere Gespräche mit der Einrichtung</div>
              <div className="rel">
                {info.related.map((r) => {
                  const by = r.repliedBy
                    ? (mailboxes.find((m) => m.address.toLowerCase() === r.repliedBy?.toLowerCase())
                        ?.displayName ?? r.repliedBy)
                    : null;
                  return (
                    <button key={r.id} className="rel-it" onClick={() => void nav(`/t/${r.id}`)}>
                      <span className="rel-r">
                        {r.crmRole ?? r.lastFrom?.name ?? r.lastFrom?.address ?? ''}
                      </span>
                      <span className="rel-w">
                        {r.lastFrom?.name ?? r.lastFrom?.address} · {fmtWhen(r.lastDate)}
                        {by ? ` · beantwortet (${by})` : ''}
                        {r.status === 'done' ? ' · erledigt' : ''}
                      </span>
                      <span className="rel-s">{r.summary ?? r.subject}</span>
                    </button>
                  );
                })}
              </div>
              {sum === 'laden' ? (
                <div className="callout bobsum">
                  <span className="spin" /> Bob liest alle Gespräche …
                </div>
              ) : sum ? (
                <div className="callout bobsum">
                  <b>✦ Bob fasst zusammen</b>
                  <br />
                  {sum.text}
                </div>
              ) : (
                <button
                  className="btn bobbtn"
                  onClick={() => {
                    setSum('laden');
                    api
                      .bobZusammenfassung(threadId)
                      .then((r) => setSum({ text: r.text }))
                      .catch(() => setSum({ text: 'Bob ist gerade nicht erreichbar.' }));
                  }}
                >
                  ✦ Was wurde besprochen?
                </button>
              )}
            </div>
          )}
          <div className="blk">
            <div className="blk-h">Erinnerungen</div>
            {k?.erinnerungen?.length ? (
              k.erinnerungen.map((r) => (
                <div key={r.id} style={{ fontSize: 13 }}>
                  ⏰ {r.titel} · {datum(r.faellig)}
                  {'fuer' in r && isUserKey(r.fuer) ? ` · ${TEAM[r.fuer].name}` : ''}
                </div>
              ))
            ) : (
              <span className="empty">keine offenen</span>
            )}
          </div>
          {k?.verlauf && k.verlauf.length > 0 && (
            <div className="blk">
              <div className="blk-h">Verlauf</div>
              <div className="tl">
                {k.verlauf.slice(0, 8).map((v, i) => (
                  <div key={i}>
                    <span>
                      {v.am.slice(8, 10)}.{v.am.slice(5, 7)}.
                    </span>
                    <span>
                      {v.text}
                      {v.wer && v.wer !== 'auto' ? ` (${v.wer})` : ''}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <a
              className="link"
              href={info.kontext?.crmUrl ?? 'https://admin.minimusiker.de'}
              target="_blank"
              rel="noopener"
            >
              Im CRM öffnen ↗
            </a>
            {buchungslink && (
              <button
                className="btn sm"
                onClick={() =>
                  void navigator.clipboard
                    .writeText(buchungslink)
                    .then(() =>
                      toast('Buchungslink kopiert – im Editor auch als Baustein /buchungslink'),
                    )
                }
                title={buchungslink}
              >
                Buchungslink kopieren
              </button>
            )}
            <button className="btn sm" onClick={() => load(true)} title="CRM-Daten neu laden">
              ↻
            </button>
          </div>
        </>
      )}
    </aside>
  );
}
